/**
 * WHICH ENVIRONMENT THIS IS, READ ONCE FROM THE SERVER AND SHARED.
 *
 * ── WHY IT IS ITS OWN PROVIDER AND NOT A FIELD ON THE WORK CONTEXT ──────────────────────────────
 *
 * `ContextProvider` carries the SESSION's working context — surface, companies, modules, periods —
 * and `contextSingleSource.test.ts` holds it to being the one source for those. The environment is
 * not a property of the session: it is the same answer before sign-in, after sign-out, and for
 * every user at once. Putting it there would widen a contract that exists to stay narrow, and the
 * sign-in screen needs this value while there is no session at all.
 *
 * ── A FAILED READ IS NOT AN ENVIRONMENT ─────────────────────────────────────────────────────────
 *
 * If `/api/portal` cannot be reached the value stays `null`, and the ribbon says "unidentified".
 * It does NOT fall back to a guess, and it does not hide — silence would be indistinguishable from
 * production, which is the one environment that renders nothing.
 */

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { callOperation } from '../api/operations';

export interface DeploymentFacts {
  /** Where this is running, as the server stated it. `null` means nobody told us. */
  readonly environment: string | null;
  /** Which release the SERVER is. Absent fields render as unavailable, never as a version. */
  readonly release: { readonly tag: string | null; readonly commit: string | null } | null;
}

const NOTHING_STATED: DeploymentFacts = { environment: null, release: null };

const EnvironmentContext = createContext<DeploymentFacts>(NOTHING_STATED);

export function EnvironmentProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [facts, setFacts] = useState<DeploymentFacts>(NOTHING_STATED);

  useEffect(() => {
    let current = true;
    void callOperation('portal.identity.read', {}).then(
      (identity) => {
        if (current) {
          setFacts({
            environment: identity.environment ?? null,
            release: identity.release ?? null,
          });
        }
      },
      () => {
        // Left null. An unreachable server has not told us where we are, and inventing an answer
        // here is exactly what the indicator exists to prevent.
        if (current) setFacts(NOTHING_STATED);
      },
    );
    return () => {
      current = false;
    };
  }, []);

  return (
    <EnvironmentContext.Provider value={facts}>{children}</EnvironmentContext.Provider>
  );
}

/**
 * What the server said about itself. Everything is `null` until it answers, and stays `null` if it
 * never does -- both mean "not stated", and neither is a licence to guess.
 */
export function useDeployment(): DeploymentFacts {
  return useContext(EnvironmentContext);
}

/** The environment alone, for the ribbon, which needs nothing else. */
export function useEnvironment(): string | null {
  return useContext(EnvironmentContext).environment;
}
